import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import '../styles.css';
import Header from './Header';
import LoadingSpinner from './LoadingSpinner';
import expensePresets from '../config/expensePresets';
import { handleDeleteExpense } from '../utils/deleteExpense';
import { detectExpenseType } from '../utils/detectExpenseType';
import { handleEditExpense } from '../utils/editExpense';
import { handleViewJustificatif } from '../utils/viewJustificatif';
import { handleAddExpenseMenu } from '../utils/addExpenseMenu';
import { handleTouchStart } from '../utils/toucheStart';
import { formatDateToDDMMYYYY } from '../utils/expenseUtils';
import { renderExpenseCard } from '../utils/renderExpenseCard';

function NoteDetails({ handleLogout, goToHome }) {
  const { noteId } = useParams();
  const navigate = useNavigate();
  const [note, setNote] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [swipedExpenses, setSwipedExpenses] = useState({});
  const [swipedRightExpenses, setSwipedRightExpenses] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);
  const touchStartX = useRef(null);
  const [noteStatus, setNoteStatus] = useState('');
  const [isSwiping, setIsSwiping] = useState(false);
  const [swipingExpenseId, setSwipingExpenseId] = useState(null);
  const swipeTimeoutRef = useRef(null);

  useEffect(() => {
    const fetchNoteAndExpenses = async () => {
      let loadingTimeout;
      
      try {
        // N'afficher le loading que si la requête prend plus de 500ms
        loadingTimeout = setTimeout(() => {
          setIsLoading(true);
        }, 500);
        
        // Ajouter un timestamp pour éviter le cache
        const timestamp = new Date().getTime();
        
        // Récupérer les détails de la note
        const noteResponse = await fetch(`/api/get-note/${noteId}?t=${timestamp}`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        });

        if (!noteResponse.ok) {
          throw new Error('Erreur lors de la récupération de la note');
        }

        const noteData = await noteResponse.json();
        setNote(noteData);

        // Récupérer les dépenses de la note
        const expensesResponse = await fetch(`/api/get-expenses/${noteId}?t=${timestamp}`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        });

        if (!expensesResponse.ok) {
          throw new Error('Erreur lors de la récupération des dépenses');
        }

        const expensesData = await expensesResponse.json();
        
        setExpenses(expensesData);
      } catch (error) {
        console.error('Erreur:', error);
        Swal.fire({
          icon: 'error',
          title: 'Erreur',
          text: 'Une erreur est survenue lors du chargement des données.',
          confirmButtonText: 'OK'
        });
      } finally {
        clearTimeout(loadingTimeout);
        setIsLoading(false);
        setHasInitialized(true);
      }
    };

    fetchNoteAndExpenses();
  }, [noteId, refreshKey]);

  useEffect(() => {
    const fetchNoteStatus = async () => {
      const noteResponse = await fetch(`/api/get-note/${noteId}`, {
        method: 'GET',
        credentials: 'include'
      });

      if (noteResponse.ok) {
        const noteData = await noteResponse.json();
        setNoteStatus(noteData.statut);
      }
    };

    fetchNoteStatus();
  }, [noteId]);

  // Effet pour recharger quand on revient sur la page
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        setRefreshKey(prev => prev + 1);
      }
    };

    const handleFocus = () => {
      setRefreshKey(prev => prev + 1);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  // Effet pour gérer le scroll du body pendant le swipe
  useEffect(() => {
    if (isSwiping) {
      document.body.classList.add('swiping');
      
      // Désactiver le mode swiping après 1 seconde si aucun vrai swipe n'est détecté
      swipeTimeoutRef.current = setTimeout(() => {
        setIsSwiping(false);
        setSwipingExpenseId(null);
      }, 1000);
    } else {
      document.body.classList.remove('swiping');
      if (swipeTimeoutRef.current) {
        clearTimeout(swipeTimeoutRef.current);
        swipeTimeoutRef.current = null;
      }
    }

    return () => {
      document.body.classList.remove('swiping');
      if (swipeTimeoutRef.current) {
        clearTimeout(swipeTimeoutRef.current);
      }
    };
  }, [isSwiping]);

  // Fonction pour gérer le clic sur une dépense en vérifiant le statut de la note
  const handleExpenseClick = (expenseId) => {
    // Vérifier si la note est payée
    if (note?.statut === 'payee' || noteStatus === 'payee') {
      Swal.fire({
        icon: 'info',
        title: 'Note payée',
        html: `
          <p>Cette note de frais a déjà été <strong>payée</strong>.</p>
          <p>Les dépenses ne peuvent plus être modifiées.</p>
        `,
        confirmButtonText: 'Compris',
        showCancelButton: false,
        allowOutsideClick: true,
        customClass: {
          popup: 'swal-note-payee'
        }
      });
      return;
    }

    // Si la note n'est pas payée, procéder à l'édition normale
    handleEditExpense(expenseId, noteId, navigate);
  };

  // Fonction pour gérer la suppression d'une dépense en vérifiant le statut de la note
  const handleExpenseDelete = (expenseId) => {
    // Vérifier si la note est payée
    if (note?.statut === 'payee' || noteStatus === 'payee') {
      Swal.fire({
        icon: 'info',
        title: 'Note payée',
        html: `
          <p>Cette note de frais a déjà été <strong>payée</strong>.</p>
          <p>Les dépenses ne peuvent plus être supprimées.</p>
        `,
        confirmButtonText: 'Compris',
        showCancelButton: false,
        allowOutsideClick: true,
        customClass: {
          popup: 'swal-note-payee'
        }
      });
      return;
    }

    // Si la note n'est pas payée, procéder à la suppression normale
    handleDeleteExpense(expenseId, noteId, setExpenses);
  };

  // Fonction pour gérer la validation d'une dépense
  const handleExpenseValidate = async (expenseId) => {
    try {
      const response = await fetch(`/api/validate-expense/${expenseId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        
        let message = 'La dépense a été validée avec succès.';
        if (result.noteStatusUpdated) {
          if (result.noteNewStatus === 'En cours') {
            message = 'La dépense a été validée avec succès. La note de frais est maintenant "En cours".';
          } else if (result.noteNewStatus === 'En attente') {
            message = 'La dépense a été validée avec succès. La note de frais est maintenant "En attente".';
          }
        }

        Swal.fire({
          icon: 'success',
          title: 'Dépense validée',
          text: message,
          confirmButtonText: 'OK'
        });
        
        // Recharger les dépenses et le statut de la note
        setRefreshKey(prev => prev + 1);
        
        // Fermer le swipe
        setSwipedRightExpenses(prev => ({
          ...prev,
          [expenseId]: false
        }));
      } else {
        throw new Error('Erreur lors de la validation');
      }
    } catch (error) {
      console.error('Erreur:', error);
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: 'Une erreur est survenue lors de la validation de la dépense.',
        confirmButtonText: 'OK'
      });
    }
  };

  // Fonction pour gérer la dévalidation d'une dépense
  const handleExpenseUnvalidate = async (expenseId) => {
    try {
      const response = await fetch(`/api/unvalidate-expense/${expenseId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        
        let message = 'La dépense a été dévalidée avec succès.';
        if (result.noteStatusUpdated) {
          if (result.noteNewStatus === 'En attente') {
            message = 'La dépense a été dévalidée avec succès. La note de frais est maintenant "En attente".';
          } else if (result.noteNewStatus === 'En cours') {
            message = 'La dépense a été dévalidée avec succès. La note de frais reste "En cours".';
          }
        }

        Swal.fire({
          icon: 'success',
          title: 'Dépense dévalidée',
          text: message,
          confirmButtonText: 'OK'
        });
        
        // Recharger les dépenses et le statut de la note
        setRefreshKey(prev => prev + 1);
        
        // Fermer le swipe
        setSwipedRightExpenses(prev => ({
          ...prev,
          [expenseId]: false
        }));
      } else {
        throw new Error('Erreur lors de la dévalidation');
      }
    } catch (error) {
      console.error('Erreur:', error);
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: 'Une erreur est survenue lors de la dévalidation de la dépense.',
        confirmButtonText: 'OK'
      });
    }
  };

  // Fonction pour valider la note de frais complète
  const handleValidateNote = async () => {
    try {
      const response = await fetch(`/api/validate-note/${noteId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        await response.json();
        
        Swal.fire({
          icon: 'success',
          title: 'Note de frais validée !',
          text: 'La note de frais a été validée avec succès et marquée comme payée.',
          confirmButtonText: 'OK'
        });
        
        // Recharger les données
        setRefreshKey(prev => prev + 1);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erreur lors de la validation');
      }
    } catch (error) {
      console.error('Erreur:', error);
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: error.message || 'Une erreur est survenue lors de la validation de la note de frais.',
        confirmButtonText: 'OK'
      });
    }
  };

  // Vérifier si toutes les dépenses sont validées
  const allExpensesValidated = expenses.length > 0 && expenses.every(expense => expense.validated === true);
  const canValidateNote = allExpensesValidated && note?.statut !== 'payee' && noteStatus !== 'payee';
  
  // Debug détaillé
  console.log('=== DEBUG BOUTON AJOUT DÉPENSE ===');
  console.log('Nombre de dépenses:', expenses.length);
  console.log('Détail des dépenses:');
  expenses.forEach((expense, index) => {
    console.log(`  Dépense ${index + 1}:`, {
      id: expense.id_depense,
      validated: expense.validated,
      typeof_validated: typeof expense.validated,
      montant: expense.montant
    });
  });

  return (
    <div>
      <main className="note-details-container">
        {/* Informations de la note */}
        
        <div className={`expenses-list ${isSwiping ? 'swiping' : ''}`}>
          {isLoading ? (
            <LoadingSpinner message="Chargement des dépenses..." />
          ) : !hasInitialized ? (
            // Ne rien afficher tant qu'on n'a pas initialisé
            null
          ) : expenses.length > 0 ? (
            <ul>
              {expenses.map(expense => {
                return (
                  <div className="note-container" key={expense.id_depense}>
                    <div 
                      className={`note-card ${swipedExpenses[expense.id_depense] ? 'swiped-left' : ''} ${swipedRightExpenses[expense.id_depense] ? 'swiped-right' : ''} ${(note?.statut === 'payee' || noteStatus === 'payee') ? 'expense-readonly' : ''} ${expense.validated ? 'expense-validated' : ''} ${swipingExpenseId === expense.id_depense ? 'swiping' : ''}`}
                      onClick={() => handleExpenseClick(expense.id_depense)}
                      onTouchStart={(e) => {
                        handleTouchStart(e, touchStartX);
                        setSwipingExpenseId(expense.id_depense);
                      }}
                      onTouchMove={(e) => {
                        if (touchStartX.current !== null) {
                          const currentX = e.touches[0].clientX;
                          const currentY = e.touches[0].clientY;
                          const deltaX = currentX - touchStartX.current;
                          const deltaY = Math.abs(currentY - (e.targetTouches[0]?.clientY || currentY));
                          
                          // Ne marquer comme swipe que si le mouvement est clairement horizontal
                          // et significatif (plus de 20px horizontal avec moins de mouvement vertical)
                          if (Math.abs(deltaX) > 20 && Math.abs(deltaX) > deltaY * 2) {
                            setIsSwiping(true);
                          }
                        }
                      }}
                      onTouchEnd={(e) => {
                        if (e && e.changedTouches && e.changedTouches[0]) {
                          const deltaX = e.changedTouches[0].clientX - touchStartX.current;
                          const isCurrentlySwipedLeft = swipedExpenses[expense.id_depense];
                          const isCurrentlySwipedRight = swipedRightExpenses[expense.id_depense];
                          
                          // Si un vrai swipe est détecté, réinitialiser le timeout
                          if (Math.abs(deltaX) > 50) {
                            if (swipeTimeoutRef.current) {
                              clearTimeout(swipeTimeoutRef.current);
                              swipeTimeoutRef.current = null;
                            }
                          }
                          
                          // Si la carte est déjà swipée vers la droite et qu'on fait un mouvement vers la gauche
                          if (isCurrentlySwipedRight && deltaX < -30) {
                            // Retour au centre
                            setSwipedExpenses(prev => ({
                              ...prev,
                              [expense.id_depense]: false
                            }));
                            setSwipedRightExpenses(prev => ({
                              ...prev,
                              [expense.id_depense]: false
                            }));
                          }
                          // Si la carte est déjà swipée vers la gauche et qu'on fait un mouvement vers la droite
                          else if (isCurrentlySwipedLeft && deltaX > 30) {
                            // Retour au centre
                            setSwipedExpenses(prev => ({
                              ...prev,
                              [expense.id_depense]: false
                            }));
                            setSwipedRightExpenses(prev => ({
                              ...prev,
                              [expense.id_depense]: false
                            }));
                          }
                          // Si la carte est au centre
                          else if (!isCurrentlySwipedLeft && !isCurrentlySwipedRight) {
                            // Swipe vers la gauche (suppression)
                            if (deltaX < -50) {
                              setSwipedExpenses(prev => ({
                                ...prev,
                                [expense.id_depense]: true
                              }));
                              setSwipedRightExpenses(prev => ({
                                ...prev,
                                [expense.id_depense]: false
                              }));
                            }
                            // Swipe vers la droite (validation/dévalidation)
                            else if (deltaX > 50) {
                              setSwipedRightExpenses(prev => ({
                                ...prev,
                                [expense.id_depense]: true
                              }));
                              setSwipedExpenses(prev => ({
                                ...prev,
                                [expense.id_depense]: false
                              }));
                            }
                          }
                          // Si le mouvement n'est pas significatif, retour au centre
                          else if (Math.abs(deltaX) < 30) {
                            setSwipedExpenses(prev => ({
                              ...prev,
                              [expense.id_depense]: false
                            }));
                            setSwipedRightExpenses(prev => ({
                              ...prev,
                              [expense.id_depense]: false
                            }));
                          }
                        }
                        // Réinitialiser l'état de swipe après un court délai
                        setTimeout(() => {
                          setIsSwiping(false);
                          setSwipingExpenseId(null);
                        }, 100);
                      }}
                    >
                      {renderExpenseCard(
                        expense,
                        detectExpenseType,
                        expensePresets,
                        formatDateToDDMMYYYY,
                        handleViewJustificatif
                      )}
                    </div>
                    
                    {/* Bouton de suppression (swipe vers la gauche) */}
                    <button 
                      className={`delete-button ${(note?.statut === 'payee' || noteStatus === 'payee') ? 'disabled' : ''}`}
                      onClick={() => handleExpenseDelete(expense.id_depense)}
                      onTouchStart={(e) => e.stopPropagation()}
                      onTouchEnd={(e) => e.stopPropagation()}
                    >
                      <i className="fa-regular fa-trash-can"></i>
                    </button>

                    {/* Bouton de validation/dévalidation (swipe vers la droite) */}
                    <button 
                      className={`validate-button ${(note?.statut === 'payee' || noteStatus === 'payee') ? 'disabled' : ''} ${expense.validated ? 'unvalidate' : ''}`}
                      onClick={() => expense.validated ? handleExpenseUnvalidate(expense.id_depense) : handleExpenseValidate(expense.id_depense)}
                      onTouchStart={(e) => e.stopPropagation()}
                      onTouchEnd={(e) => e.stopPropagation()}
                    >
                      <i className={`fa-solid ${expense.validated ? 'fa-times' : 'fa-check'}`}></i>
                    </button>
                  </div>
                );
              })}
            </ul>
          ) : (
            <div className="empty-state">
              <div className="empty-icon">
                <i className="fa-solid fa-receipt"></i>
              </div>
              <h3>Aucune dépense</h3>
              <p>Aucune dépense n'a encore été enregistrée pour cette note de frais.</p>
            </div>
          )}
        </div>

        {/* Bouton de validation de la note de frais */}
        {canValidateNote && (
          <button 
            className="validate-note-button"
            onClick={handleValidateNote}
          >
            <i className="fa-solid fa-check-double"></i>
          </button>
        )}
      </main>

      <footer>
        <Header
          handleLogout={handleLogout}
          leftActionButton={(
            <button
              className="home-notes-button"
              onClick={() => navigate('/notes-list')}
              aria-label="Retour aux notes de frais"
              title="Retour aux notes de frais"
            >
              <i className="fa-solid fa-xmark"></i>
            </button>
          )}
        />
        <button 
          onClick={() => handleAddExpenseMenu(navigate, noteId)} 
          className={`footer-center-button footer-submit-button ${(noteStatus === 'payee' || allExpensesValidated) ? 'disabled-button' : ''}`}
          aria-label="Ajouter une dépense"
          title="Ajouter une dépense"
          disabled={noteStatus === 'payee' || allExpensesValidated}
        >
          <i className="fa-solid fa-plus"></i>
        </button>
      </footer>
    </div>
  );
}

export default NoteDetails; 
